import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  message, Tabs, Tag, Space, Popconfirm, Descriptions, Row, Col,
  Statistic, Progress, Divider, Tooltip, Alert
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  CheckOutlined, CloseOutlined, DollarCircleOutlined,
  PieChartOutlined, BarChartOutlined, FileTextOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Project, ProjectExpense, ExpenseCategory, ProjectBudget, ProjectCostSummary,
  ProjectExpenseCreate, ProjectExpenseUpdate, ProjectBudgetCreate, ProjectBudgetUpdate,
  ExpenseStatus, ProjectRevenue, ProjectRevenueCreate, ProjectRevenueUpdate, 
  RevenueType, ProjectFinancialSummary
} from '@/types';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

interface ProjectWithCosts extends Project {
  costSummary?: ProjectCostSummary | null;
}

interface ProjectCostsState {
  projects: ProjectWithCosts[];
  selectedProject: ProjectWithCosts | null;
  expenses: ProjectExpense[];
  revenues: ProjectRevenue[];
  categories: ExpenseCategory[];
  budget: ProjectBudget | null;
  costSummary: ProjectCostSummary | null;
  financialSummary: ProjectFinancialSummary | null;
  loading: boolean;
  expenseModalVisible: boolean;
  revenueModalVisible: boolean;
  budgetModalVisible: boolean;
  expenseDetailModalVisible: boolean;
  revenueDetailModalVisible: boolean;
  selectedExpense: ProjectExpense | null;
  selectedRevenue: ProjectRevenue | null;
  editingRevenue: ProjectRevenue | null;
  expenseForm: any;
  budgetForm: any;
}

const ProjectCosts: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<ProjectCostsState>({
    projects: [],
    selectedProject: null,
    expenses: [],
    revenues: [],
    categories: [],
    budget: null,
    costSummary: null,
    financialSummary: null,
    loading: false,
    expenseModalVisible: false,
    revenueModalVisible: false,
    budgetModalVisible: false,
    expenseDetailModalVisible: false,
    revenueDetailModalVisible: false,
    selectedExpense: null,
    selectedRevenue: null,
    editingRevenue: null,
    expenseForm: null,
    budgetForm: null
  });

  const [expenseForm] = Form.useForm();
  const [budgetForm] = Form.useForm();
  const [revenueForm] = Form.useForm();
  
  // 内部成本计算状态
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [calculationModalVisible, setCalculationModalVisible] = useState(false);
  const [calculationForm] = Form.useForm();
  
  // 调度器状态
  const [schedulerStatus, setSchedulerStatus] = useState<any>(null);
  const [schedulerStatusLoading, setSchedulerStatusLoading] = useState(false);

  useEffect(() => {
    loadProjects();
    loadCategories();
    loadSchedulerStatus();
  }, []);

  const loadSchedulerStatus = async () => {
    if (user?.role !== 'admin') return;
    
    try {
      setSchedulerStatusLoading(true);
      const status = await api.getSchedulerStatus();
      setSchedulerStatus(status);
    } catch (error) {
      console.error('获取调度器状态失败:', error);
    } finally {
      setSchedulerStatusLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const projects = await api.getCostAccessibleProjects();
      
      // Load cost summary for all projects immediately
      const projectsWithCosts = await Promise.all(
        projects.map(async (project) => {
          try {
            const costSummary = await api.getProjectCostSummary(project.id);
            return { ...project, costSummary };
          } catch (error) {
            console.error(`Failed to load cost summary for project ${project.id}:`, error);
            return { ...project, costSummary: null };
          }
        })
      );

      setState(prev => ({
        ...prev,
        projects: projectsWithCosts,
        loading: false
      }));
    } catch (error) {
      message.error('加载项目列表失败');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadCategories = async () => {
    try {
      const categories = await api.getExpenseCategories();
      setState(prev => ({ ...prev, categories }));
    } catch (error) {
      console.error('加载支出类别失败:', error);
    }
  };

  const loadProjectData = async (projectId: number) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      const [expenses, revenues, budgetInfo, costSummary, financialSummary] = await Promise.all([
        api.getProjectExpenses(projectId),
        api.getProjectRevenues({ project_id: projectId, limit: 100 }).then(res => res.revenues),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/expenses/projects/${projectId}/budget`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }).then(res => res.json()),
        api.getProjectCostSummary(projectId),
        api.getProjectFinancialSummary(projectId).catch(() => null)
      ]);

      setState(prev => ({
        ...prev,
        expenses: expenses || [],
        revenues: revenues || [],
        budget: budgetInfo.has_budget ? { 
          id: 1, 
          project_id: projectId,
          total_budget: budgetInfo.budget,
          budget_year: new Date().getFullYear(),
          notes: "项目预算"
        } : null,
        costSummary,
        financialSummary,
        loading: false
      }));
    } catch (error) {
      message.error('加载项目数据失败');
      setState(prev => ({ ...prev, loading: false }));
    }
  };



  const handleCreateExpense = async (values: any) => {
    try {
      const expenseData: ProjectExpenseCreate = {
        project_id: state.selectedProject!.id,
        category_id: values.category_id,
        title: values.title,
        description: values.description,
        amount: values.amount,
        expense_date: values.expense_date.format('YYYY-MM-DD')
      };

      await api.createProjectExpense(expenseData);
      message.success('支出记录创建成功');
      setState(prev => ({ ...prev, expenseModalVisible: false }));
      expenseForm.resetFields();
      if (state.selectedProject) {
        loadProjectData(state.selectedProject.id);
      }
    } catch (error) {
      message.error('创建支出记录失败');
    }
  };

  const handleUpdateExpense = async (values: any) => {
    if (!state.selectedExpense) return;

    try {
      const expenseData: ProjectExpenseUpdate = {
        category_id: values.category_id,
        title: values.title,
        description: values.description,
        amount: values.amount,
        expense_date: values.expense_date.format('YYYY-MM-DD')
      };

      await api.updateProjectExpense(state.selectedExpense.id, expenseData);
      message.success('支出记录更新成功');
      setState(prev => ({ ...prev, expenseModalVisible: false, selectedExpense: null }));
      expenseForm.resetFields();
      if (state.selectedProject) {
        loadProjectData(state.selectedProject.id);
      }
    } catch (error) {
      message.error('更新支出记录失败');
    }
  };

  const handleDeleteExpense = async (expenseId: number) => {
    try {
      await api.deleteProjectExpense(expenseId);
      message.success('支出记录删除成功');
      if (state.selectedProject) {
        loadProjectData(state.selectedProject.id);
      }
    } catch (error) {
      message.error('删除支出记录失败');
    }
  };

  const handleApproveExpense = async (expenseId: number, status: 'approved' | 'rejected') => {
    try {
      await api.approveExpense(expenseId, {
        status,
        approval_notes: status === 'rejected' ? '管理员拒绝' : '管理员批准'
      });
      message.success(`支出记录已${status === 'approved' ? '批准' : '拒绝'}`);
      if (state.selectedProject) {
        loadProjectData(state.selectedProject.id);
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleSetBudget = async (values: any) => {
    try {
      const budgetData = {
        budget: values.budget
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/expenses/projects/${state.selectedProject!.id}/budget`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(budgetData)
      });

      if (!response.ok) {
        throw new Error('预算设置失败');
      }

      message.success('预算设置成功');
      setState(prev => ({ ...prev, budgetModalVisible: false }));
      budgetForm.resetFields();
      if (state.selectedProject) {
        await loadProjectData(state.selectedProject.id);
        // 重新加载项目列表以更新预算状态
        loadProjects();
      }
    } catch (error) {
      message.error('设置预算失败');
    }
  };

  const openExpenseModal = (expense?: ProjectExpense) => {
    if (expense) {
      setState(prev => ({ ...prev, selectedExpense: expense }));
      expenseForm.setFieldsValue({
        category_id: expense.category_id,
        title: expense.title,
        description: expense.description,
        amount: expense.amount,
        expense_date: dayjs(expense.expense_date)
      });
    } else {
      setState(prev => ({ ...prev, selectedExpense: null }));
      expenseForm.resetFields();
    }
    setState(prev => ({ ...prev, expenseModalVisible: true }));
  };

  const openBudgetModal = () => {
    if (state.budget) {
      budgetForm.setFieldsValue({
        budget: state.budget.total_budget
      });
    } else {
      budgetForm.resetFields();
    }
    setState(prev => ({ ...prev, budgetModalVisible: true }));
  };

  const openExpenseDetailModal = (expense: ProjectExpense) => {
    setState(prev => ({
      ...prev,
      selectedExpense: expense,
      expenseDetailModalVisible: true
    }));
  };

  // 收入相关功能
  const handleCreateRevenue = async (values: any) => {
    try {
      const revenueData: ProjectRevenueCreate = {
        project_id: state.selectedProject!.id,
        amount: values.amount,
        revenue_date: values.revenue_date.format('YYYY-MM-DD'),
        description: values.description,
        revenue_type: values.revenue_type || 'contract_payment'
      };

      await api.createProjectRevenue(revenueData);
      message.success('收入记录创建成功');
      
      setState(prev => ({ ...prev, revenueModalVisible: false }));
      revenueForm.resetFields();
      
      if (state.selectedProject) {
        await loadProjectData(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '创建收入记录失败');
    }
  };

  const handleUpdateRevenue = async (values: any) => {
    if (!state.editingRevenue) return;
    
    try {
      const updateData: ProjectRevenueUpdate = {
        amount: values.amount,
        revenue_date: values.revenue_date.format('YYYY-MM-DD'),
        description: values.description,
        revenue_type: values.revenue_type
      };

      await api.updateProjectRevenue(state.editingRevenue.id, updateData);
      message.success('收入记录更新成功');
      
      setState(prev => ({ ...prev, revenueModalVisible: false, editingRevenue: null }));
      revenueForm.resetFields();
      
      if (state.selectedProject) {
        await loadProjectData(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '更新收入记录失败');
    }
  };

  const handleDeleteRevenue = async (revenueId: number) => {
    try {
      await api.deleteProjectRevenue(revenueId);
      message.success('收入记录删除成功');
      
      if (state.selectedProject) {
        await loadProjectData(state.selectedProject.id);
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '删除收入记录失败');
    }
  };

  const openRevenueModal = (revenue?: ProjectRevenue) => {
    if (revenue) {
      setState(prev => ({ ...prev, editingRevenue: revenue }));
      revenueForm.setFieldsValue({
        amount: revenue.amount,
        revenue_date: dayjs(revenue.revenue_date),
        description: revenue.description,
        revenue_type: revenue.revenue_type
      });
    } else {
      setState(prev => ({ ...prev, editingRevenue: null }));
      revenueForm.resetFields();
      revenueForm.setFieldsValue({
        revenue_type: 'contract_payment',
        revenue_date: dayjs()
      });
    }
    setState(prev => ({ ...prev, revenueModalVisible: true }));
  };

  const openRevenueDetailModal = (revenue: ProjectRevenue) => {
    setState(prev => ({
      ...prev,
      selectedRevenue: revenue,
      revenueDetailModalVisible: true
    }));
  };

  // 内部成本计算功能
  const handleCalculateInternalCosts = async (values: any) => {
    setCalculationLoading(true);
    try {
      const result = await api.manualCalculateCosts({
        target_date: values.target_date.format('YYYY-MM-DD'),
        force_recalculate: values.force_recalculate || false
      });
      
      if (result.success) {
        message.success(`成功计算内部成本！处理了${result.users_processed}个用户，创建${result.records_created}条记录，更新${result.records_updated}条记录`);
        setCalculationModalVisible(false);
        calculationForm.resetFields();
        
        // 如果当前有选中的项目，刷新其成本数据
        if (state.selectedProject) {
          loadProjectData(state.selectedProject.id);
        }
        
        // 刷新项目列表
        loadProjects();
      } else {
        message.error(`计算失败：${result.error}`);
      }
    } catch (error) {
      message.error('计算内部成本时发生错误');
      console.error(error);
    } finally {
      setCalculationLoading(false);
    }
  };

  const handleCalculateYesterday = async () => {
    setCalculationLoading(true);
    try {
      const result = await api.calculateYesterdayCosts(false);
      
      if (result.success) {
        message.success(`成功计算昨天的内部成本！处理了${result.users_processed}个用户，创建了${result.records_created}条记录`);
        // 刷新数据
        if (state.selectedProject) {
          loadProjectData(state.selectedProject.id);
        }
        loadProjects();
      } else {
        message.error(`计算失败：${result.error}`);
      }
    } catch (error) {
      message.error('计算昨天内部成本时发生错误');
      console.error(error);
    } finally {
      setCalculationLoading(false);
    }
  };

  const handleGenerateExpenseRecords = async () => {
    if (!state.selectedProject) return;
    
    try {
      const yesterday = dayjs().subtract(1, 'day');
      const result = await api.generateInternalExpenseRecords({
        target_date: yesterday.format('YYYY-MM-DD'),
        force_recreate: false
      });
      
      if (result.success) {
        message.success(`成功生成支出记录！创建${result.expenses_created}条，更新${result.expenses_updated}条`);
        loadProjectData(state.selectedProject.id);
      } else {
        message.error(`生成失败：${result.error}`);
      }
    } catch (error) {
      message.error('生成支出记录时发生错误');
      console.error(error);
    }
  };

  const getStatusColor = (status: ExpenseStatus) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'approved': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: ExpenseStatus) => {
    switch (status) {
      case 'pending': return '待审批';
      case 'approved': return '已批准';
      case 'rejected': return '已拒绝';
      default: return status;
    }
  };

  const getRevenueTypeText = (type: RevenueType) => {
    const typeMap = {
      contract_payment: '合同付款',
      milestone_payment: '里程碑付款',
      additional_service: '增值服务',
      other: '其他'
    };
    return typeMap[type] || type;
  };

  const expenseColumns: ColumnsType<ProjectExpense> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '类别',
      dataIndex: ['category', 'name'],
      key: 'category',
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number | string) => `¥${typeof amount === 'number' ? amount.toFixed(2) : parseFloat(amount || '0').toFixed(2)}`,
    },
    {
      title: '日期',
      dataIndex: 'expense_date',
      key: 'expense_date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: ExpenseStatus) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: '提交人',
      dataIndex: ['user', 'real_name'],
      key: 'user',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, expense) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => openExpenseDetailModal(expense)}
            />
          </Tooltip>
          {expense.status === 'pending' && (
            <>
              <Tooltip title="编辑">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openExpenseModal(expense)}
                />
              </Tooltip>
              <Tooltip title="批准">
                <Popconfirm
                  title="确认批准这条支出记录？"
                  onConfirm={() => handleApproveExpense(expense.id, 'approved')}
                >
                  <Button type="text" icon={<CheckOutlined />} />
                </Popconfirm>
              </Tooltip>
              <Tooltip title="拒绝">
                <Popconfirm
                  title="确认拒绝这条支出记录？"
                  onConfirm={() => handleApproveExpense(expense.id, 'rejected')}
                >
                  <Button type="text" icon={<CloseOutlined />} />
                </Popconfirm>
              </Tooltip>
            </>
          )}
          <Tooltip title="删除">
            <Popconfirm
              title="确认删除这条支出记录？"
              onConfirm={() => handleDeleteExpense(expense.id)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const revenueColumns: ColumnsType<ProjectRevenue> = [
    {
      title: '收入金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount.toLocaleString()}`,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: '收入日期',
      dataIndex: 'revenue_date',
      key: 'revenue_date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a, b) => dayjs(a.revenue_date).unix() - dayjs(b.revenue_date).unix(),
    },
    {
      title: '收入类型',
      dataIndex: 'revenue_type',
      key: 'revenue_type',
      render: (type: RevenueType) => getRevenueTypeText(type),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: { showTitle: false },
      render: (description: string) => (
        <Tooltip placement="topLeft" title={description}>
          {description || '-'}
        </Tooltip>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, revenue) => (
        <Space>
          <Tooltip title="查看详情">
            <Button 
              type="text" 
              icon={<EyeOutlined />}
              onClick={() => openRevenueDetailModal(revenue)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button 
              type="text" 
              icon={<EditOutlined />}
              onClick={() => openRevenueModal(revenue)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确认删除这条收入记录？"
              onConfirm={() => handleDeleteRevenue(revenue.id)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const renderFinancialSummary = () => {
    if (!state.costSummary && !state.financialSummary) return null;

    const { costSummary, financialSummary } = state;
    const budgetUsage = costSummary?.budget_usage || 0;
    
    // 如果有财务汇总数据，优先使用，否则使用成本汇总数据
    const totalRevenue = financialSummary?.total_revenue || 0;
    const totalCost = financialSummary?.total_cost || costSummary?.total_cost || 0;
    const internalCost = financialSummary?.internal_cost || costSummary?.internal_cost || 0;
    const externalCost = financialSummary?.external_cost || costSummary?.external_expense_cost || 0;
    const netProfit = financialSummary?.net_profit || (totalRevenue - totalCost);
    const profitMargin = financialSummary?.profit_margin || (totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0);

    return (
      <>
        {/* 财务汇总（包含收入和成本） */}
        {totalRevenue > 0 && (
          <Card 
            title={<><DollarCircleOutlined /> 财务汇总</>} 
            style={{ marginBottom: 16 }}
            bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总收入"
                  value={totalRevenue}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总成本"
                  value={totalCost}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#f5222d' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="净利润"
                  value={netProfit}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: netProfit >= 0 ? '#52c41a' : '#f5222d' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="利润率"
                  value={profitMargin}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: profitMargin >= 0 ? '#52c41a' : '#f5222d' }}
                />
              </Col>
            </Row>
          </Card>
        )}
        
        {/* 成本明细 */}
        <Card 
          title={<><PieChartOutlined /> 成本明细</>} 
          style={{ marginBottom: 16 }}
          bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="内部费用"
                value={internalCost}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="外部支出"
                value={externalCost}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="总成本"
                value={totalCost}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#f5222d' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="预算使用率"
                value={budgetUsage * 100}
                precision={1}
                suffix="%"
              />
              <Progress percent={budgetUsage * 100} size="small" />
            </Col>
          </Row>
          
          {/* 内部费用明细 */}
          {costSummary?.internal_cost_summary && costSummary.internal_cost_summary.total_cost > 0 && (
            <>
              <Divider />
              <h4 style={{ color: '#1890ff' }}>内部费用明细</h4>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Statistic
                    title="总工时"
                    value={costSummary.internal_cost_summary.total_hours}
                    precision={1}
                    suffix="小时"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="参与人数"
                    value={costSummary.internal_cost_summary.user_count}
                    suffix="人"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="内部总成本"
                    value={costSummary.internal_cost_summary.total_cost}
                    precision={2}
                    prefix="¥"
                  />
                </Col>
              </Row>
              {Object.keys(costSummary.internal_cost_summary.by_user).length > 0 && (
                <>
                  <h5>按人员统计</h5>
                  <Row gutter={16}>
                    {Object.entries(costSummary.internal_cost_summary.by_user).map(([userName, userStats]) => (
                      <Col span={8} key={userName} style={{ marginBottom: 16 }}>
                        <div style={{ 
                          padding: '12px', 
                          border: '1px solid #d9d9d9', 
                          borderRadius: '6px',
                          backgroundColor: '#fafafa'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{userName}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            工时: {userStats.total_hours?.toFixed(1)}小时 | 
                            工作天数: {userStats.work_days}天
                          </div>
                          <div style={{ fontSize: '14px', color: '#1890ff', fontWeight: 'bold' }}>
                            ¥{userStats.total_cost?.toFixed(2)}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </>
          )}
        
          {/* 外部支出分类统计 */}
          {costSummary && Object.keys(costSummary.expense_by_category).length > 0 && (
            <>
              <Divider />
              <h4 style={{ color: '#52c41a' }}>外部支出分类统计</h4>
              <Row gutter={16}>
                {Object.entries(costSummary.expense_by_category).map(([category, amount]) => (
                  <Col span={8} key={category} style={{ marginBottom: 16 }}>
                    <Statistic
                      title={category}
                      value={amount}
                      precision={2}
                      prefix="¥"
                      valueStyle={{ color: category === '内部费用' ? '#1890ff' : '#52c41a' }}
                    />
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Card>
      </>
    );
  };

  const renderBudgetInfo = () => {
    const isAdmin = user?.role === 'admin';
    
    if (!state.budget) {
      return (
        <Card title={<><DollarCircleOutlined /> 项目预算</>} style={{ marginBottom: 24 }}>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p>未设置预算</p>
            {isAdmin && (
              <Button type="primary" onClick={openBudgetModal}>
                设置预算
              </Button>
            )}
          </div>
        </Card>
      );
    }
    
    return (
      <Card
        title={<><DollarCircleOutlined /> 项目预算</>}
        extra={isAdmin && <Button type="link" onClick={openBudgetModal}>编辑</Button>}
        style={{ marginBottom: 16 }}
        bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="预算金额">
            ¥{typeof state.budget.total_budget === 'number' ? state.budget.total_budget.toFixed(2) : '0.00'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  };

  const renderProjectCard = (project: ProjectWithCosts) => {
    const costSummary = project.costSummary;
    const budgetUsage = costSummary?.budget_usage || 0;
    const hasBudget = costSummary?.total_budget && costSummary?.total_budget > 0;
    
    return (
      <Card
        key={project.id}
        style={{ marginBottom: 16 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{project.name}</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {!hasBudget && (
                <Tag color="orange" icon={<DollarCircleOutlined />}>
                  未设置预算
                </Tag>
              )}
              <Tag color={project.status === 'completed' ? 'green' : project.status === 'in_progress' ? 'blue' : 'orange'}>
                {project.status === 'completed' ? '已完成' : project.status === 'in_progress' ? '进行中' : '未开始'}
              </Tag>
            </div>
          </div>
        }
        extra={
          <Space>
            {!hasBudget && user?.role === 'admin' && (
              <Button
                type="primary"
                size="small"
                ghost
                icon={<DollarCircleOutlined />}
                onClick={() => {
                  setState(prev => ({ ...prev, selectedProject: project }));
                  loadProjectData(project.id);
                  // 延迟打开预算设置模态框
                  setTimeout(() => {
                    openBudgetModal();
                  }, 500);
                }}
              >
                设置预算
              </Button>
            )}
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setState(prev => ({ ...prev, selectedProject: project }));
                loadProjectData(project.id);
              }}
            >
              查看详情
            </Button>
          </Space>
        }
      >
        {costSummary ? (
          <>
            <Row gutter={16}>
              <Col span={hasBudget ? 6 : 8}>
                <Statistic
                  title="内部费用"
                  value={costSummary.internal_cost}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ fontSize: 16, color: '#1890ff' }}
                />
              </Col>
              <Col span={hasBudget ? 6 : 8}>
                <Statistic
                  title="外部支出"
                  value={costSummary.external_expense_cost}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ fontSize: 16, color: '#52c41a' }}
                />
              </Col>
              <Col span={hasBudget ? 6 : 8}>
                <Statistic
                  title="总成本"
                  value={costSummary.total_cost}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ fontSize: 16, color: '#f5222d' }}
                />
              </Col>
              {hasBudget && (
                <Col span={6}>
                  <Statistic
                    title="预算使用率"
                    value={budgetUsage * 100}
                    precision={1}
                    suffix="%"
                    valueStyle={{ 
                      fontSize: 16,
                      color: budgetUsage > 0.9 ? '#ff4d4f' : budgetUsage > 0.7 ? '#faad14' : '#52c41a'
                    }}
                  />
                  {budgetUsage > 0 && (
                    <Progress 
                      percent={budgetUsage * 100} 
                      size="small" 
                      strokeColor={budgetUsage > 0.9 ? '#ff4d4f' : budgetUsage > 0.7 ? '#faad14' : '#52c41a'}
                      style={{ marginTop: 4 }}
                    />
                  )}
                </Col>
              )}
            </Row>
            {!hasBudget && (
              <div style={{ 
                marginTop: 16, 
                padding: '12px', 
                backgroundColor: '#fff7e6', 
                border: '1px solid #ffd591', 
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#fa8c16', marginBottom: 4 }}>
                  <DollarCircleOutlined style={{ marginRight: 4 }} />
                  该项目尚未设置预算
                </div>
                <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                  设置预算后可查看预算使用率和进度
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', color: '#999' }}>
            暂无成本数据
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 24 }}>项目成本与收入管理</h2>
        {user?.role === 'admin' && (
          <Space>
            <Button
              type="primary"
              ghost
              loading={calculationLoading}
              onClick={handleCalculateYesterday}
            >
              计算昨天成本
            </Button>
            <Button
              type="primary"
              onClick={() => setCalculationModalVisible(true)}
            >
              成本计算工具
            </Button>
          </Space>
        )}
      </div>

      {/* 自动调度器状态卡片 (仅管理员可见) */}
      {user?.role === 'admin' && schedulerStatus && (
        <Card
          size="small"
          style={{ marginBottom: 16, backgroundColor: schedulerStatus.running ? '#f6ffed' : '#fff1f0' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 14 }}>
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: schedulerStatus.running ? '#52c41a' : '#ff4d4f',
                marginRight: 8
              }} />
              自动成本计算调度器
            </div>
          }
          extra={
            <Space size="small">
              <Button
                size="small"
                onClick={loadSchedulerStatus}
                loading={schedulerStatusLoading}
              >
                刷新状态
              </Button>
            </Space>
          }
        >
          <div style={{ fontSize: 12 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <div>
                <strong>状态：</strong>
                <Tag color={schedulerStatus.running ? 'success' : 'error'} size="small">
                  {schedulerStatus.running ? '运行中' : '已停止'}
                </Tag>
              </div>
              <div>
                <strong>执行时间：</strong>{schedulerStatus.scheduler_info?.execution_time}
              </div>
              <div>
                <strong>下次执行：</strong>{schedulerStatus.system_info?.next_execution}
              </div>
              <div style={{ color: '#666' }}>
                {schedulerStatus.scheduler_info?.task}
              </div>
            </Space>
          </div>
        </Card>
      )}

      {state.loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            加载中...
          </div>
        </Card>
      ) : state.projects.length > 0 ? (
        <>
          {state.projects.map(project => renderProjectCard(project))}
        </>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p>暂无可查看的项目</p>
            <p style={{ color: '#999', fontSize: 14 }}>
              管理员可以查看所有项目成本，项目负责人可以查看自己负责的项目成本
            </p>
          </div>
        </Card>
      )}

      {state.selectedProject && (
        <Modal
          title={`${state.selectedProject.name} - 详细成本信息`}
          open={!!state.selectedProject}
          onCancel={() => setState(prev => ({ ...prev, selectedProject: null }))}
          footer={null}
          width="90%"
          style={{ maxWidth: '1200px', top: 20 }}
        >
          <Tabs defaultActiveKey="summary">
            <TabPane tab={<><BarChartOutlined /> 成本汇总</>} key="summary">
              {renderFinancialSummary()}
              {renderBudgetInfo()}
            </TabPane>
            
            <TabPane tab={<><FileTextOutlined /> 支出记录</>} key="expenses">
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openExpenseModal()}
                >
                  添加支出
                </Button>
              </div>
              <Table
                columns={expenseColumns}
                dataSource={state.expenses}
                rowKey="id"
                loading={state.loading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>
            
            <TabPane tab={<><DollarCircleOutlined /> 收入记录</>} key="revenues">
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openRevenueModal()}
                >
                  添加收入
                </Button>
              </div>
              <Table
                columns={revenueColumns}
                dataSource={state.revenues}
                rowKey="id"
                loading={state.loading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>
          </Tabs>
        </Modal>
      )}

      {/* 支出记录模态框 */}
      <Modal
        title={state.selectedExpense ? '编辑支出记录' : '添加支出记录'}
        open={state.expenseModalVisible}
        onCancel={() => setState(prev => ({ ...prev, expenseModalVisible: false, selectedExpense: null }))}
        footer={null}
        width={480}
        zIndex={2000}
      >
        <Form
          form={expenseForm}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          onFinish={state.selectedExpense ? handleUpdateExpense : handleCreateExpense}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入支出标题' }]}
          >
            <Input placeholder="输入支出标题" />
          </Form.Item>
          
          <Form.Item
            name="category_id"
            label="支出类别"
            rules={[{ required: true, message: '请选择支出类别' }]}
          >
            <Select placeholder="选择支出类别">
              <Option value={1}>开发成本</Option>
              <Option value={2}>网络成本</Option>
              <Option value={3}>服务成本</Option>
              <Option value={4}>资源购买</Option>
              <Option value={5}>硬件成本</Option>
              <Option value={6}>加工成本</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="输入金额"
              min={0}
              precision={2}
            />
          </Form.Item>
          
          <Form.Item
            name="expense_date"
            label="支出日期"
            rules={[{ required: true, message: '请选择支出日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item name="description" label="描述">
            <TextArea rows={2} placeholder="输入支出描述" />
          </Form.Item>
          
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {state.selectedExpense ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setState(prev => ({ ...prev, expenseModalVisible: false, selectedExpense: null }))}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 预算设置模态框 */}
      <Modal
        title={state.budget ? '修改项目预算' : '设置项目预算'}
        open={state.budgetModalVisible}
        onCancel={() => setState(prev => ({ ...prev, budgetModalVisible: false }))}
        footer={null}
        width={400}
        zIndex={2000}
      >
        <Form
          form={budgetForm}
          layout="vertical"
          onFinish={handleSetBudget}
        >
          <Form.Item
            name="budget"
            label="项目预算"
            rules={[{ required: true, message: '请输入项目预算' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="输入项目预算"
              min={0}
              precision={2}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/¥\s?|(,*)/g, '')}
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {state.budget ? '更新' : '设置'}
              </Button>
              <Button onClick={() => setState(prev => ({ ...prev, budgetModalVisible: false }))}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 支出详情模态框 */}
      <Modal
        title="支出详情"
        open={state.expenseDetailModalVisible}
        onCancel={() => setState(prev => ({ ...prev, expenseDetailModalVisible: false, selectedExpense: null }))}
        footer={
          <Button onClick={() => setState(prev => ({ ...prev, expenseDetailModalVisible: false, selectedExpense: null }))}>
            关闭
          </Button>
        }
        width={600}
        zIndex={2000}
      >
        {state.selectedExpense && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="标题">{state.selectedExpense.title}</Descriptions.Item>
            <Descriptions.Item label="类别">
              {state.selectedExpense.category?.name}
            </Descriptions.Item>
            <Descriptions.Item label="金额">
              ¥{typeof state.selectedExpense.amount === 'number' ? state.selectedExpense.amount.toFixed(2) : '0.00'}
            </Descriptions.Item>
            <Descriptions.Item label="日期">
              {dayjs(state.selectedExpense.expense_date).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={getStatusColor(state.selectedExpense.status)}>
                {getStatusText(state.selectedExpense.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="提交人">
              {state.selectedExpense.user?.real_name}
            </Descriptions.Item>
            {state.selectedExpense.description && (
              <Descriptions.Item label="描述">
                {state.selectedExpense.description}
              </Descriptions.Item>
            )}
            {state.selectedExpense.approved_by && (
              <Descriptions.Item label="审批人">
                {state.selectedExpense.approver?.real_name}
              </Descriptions.Item>
            )}
            {state.selectedExpense.approved_at && (
              <Descriptions.Item label="审批时间">
                {dayjs(state.selectedExpense.approved_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            )}
            {state.selectedExpense.approval_notes && (
              <Descriptions.Item label="审批备注">
                {state.selectedExpense.approval_notes}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">
              {dayjs(state.selectedExpense.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 收入记录模态框 */}
      <Modal
        title={state.editingRevenue ? '编辑收入记录' : '添加收入记录'}
        open={state.revenueModalVisible}
        onCancel={() => setState(prev => ({ ...prev, revenueModalVisible: false, editingRevenue: null }))}
        footer={null}
        width={480}
        zIndex={2000}
      >
        <Form
          form={revenueForm}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          onFinish={state.editingRevenue ? handleUpdateRevenue : handleCreateRevenue}
        >
          <Form.Item
            name="amount"
            label="收入金额"
            rules={[{ required: true, message: '请输入收入金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="输入收入金额"
              min={0}
              precision={2}
              formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/¥\s?|(,*)/g, '')}
            />
          </Form.Item>
          
          <Form.Item
            name="revenue_type"
            label="收入类型"
            rules={[{ required: true, message: '请选择收入类型' }]}
          >
            <Select placeholder="选择收入类型">
              <Option value="contract_payment">合同付款</Option>
              <Option value="milestone_payment">里程碑付款</Option>
              <Option value="additional_service">增值服务</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="revenue_date"
            label="收入日期"
            rules={[{ required: true, message: '请选择收入日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="输入收入描述（可选）" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {state.editingRevenue ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setState(prev => ({ ...prev, revenueModalVisible: false, editingRevenue: null }))}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 收入详情模态框 */}
      <Modal
        title="收入详情"
        open={state.revenueDetailModalVisible}
        onCancel={() => setState(prev => ({ ...prev, revenueDetailModalVisible: false, selectedRevenue: null }))}
        footer={
          <Button onClick={() => setState(prev => ({ ...prev, revenueDetailModalVisible: false, selectedRevenue: null }))}>
            关闭
          </Button>
        }
        width={600}
        zIndex={2000}
      >
        {state.selectedRevenue && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="收入金额">
              ¥{state.selectedRevenue.amount.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="收入类型">
              {getRevenueTypeText(state.selectedRevenue.revenue_type)}
            </Descriptions.Item>
            <Descriptions.Item label="收入日期">
              {dayjs(state.selectedRevenue.revenue_date).format('YYYY-MM-DD')}
            </Descriptions.Item>
            {state.selectedRevenue.description && (
              <Descriptions.Item label="描述">
                {state.selectedRevenue.description}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建人">
              {state.selectedRevenue.creator?.real_name || '未知'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(state.selectedRevenue.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 内部成本计算工具模态框 */}
      {user?.role === 'admin' && (
        <Modal
          title="内部成本计算工具"
          open={calculationModalVisible}
          onCancel={() => {
            setCalculationModalVisible(false);
            calculationForm.resetFields();
          }}
          footer={null}
          width={500}
        >
          <Alert
            message="成本计算说明"
            description="此工具会根据工作记录自动计算员工的内部成本，并可选择生成支出记录。请谨慎使用，避免重复计算。"
            type="info"
            style={{ marginBottom: 24 }}
          />
          
          <Form
            form={calculationForm}
            layout="vertical"
            onFinish={handleCalculateInternalCosts}
          >
            <Form.Item
              name="target_date"
              label="计算日期"
              rules={[{ required: true, message: '请选择计算日期' }]}
            >
              <DatePicker 
                style={{ width: '100%' }}
                placeholder="选择要计算成本的日期"
                disabledDate={(current) => current && current > dayjs().subtract(1, 'day')}
              />
            </Form.Item>
            
            <Form.Item
              name="force_recalculate"
              valuePropName="checked"
            >
              <Space>
                <input type="checkbox" />
                <span>强制重新计算（会覆盖已有数据）</span>
              </Space>
            </Form.Item>
            
            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={calculationLoading}
                >
                  开始计算
                </Button>
                <Button 
                  onClick={() => {
                    setCalculationModalVisible(false);
                    calculationForm.resetFields();
                  }}
                >
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
          
          <Divider />
          
          <div>
            <h4>项目相关操作</h4>
            {state.selectedProject && (
              <div style={{ marginBottom: 16 }}>
                <p>当前项目: <strong>{state.selectedProject.name}</strong></p>
                <Space>
                  <Button
                    type="default"
                    onClick={handleGenerateExpenseRecords}
                    disabled={calculationLoading}
                  >
                    生成内部费用记录
                  </Button>
                </Space>
              </div>
            )}
            {!state.selectedProject && (
              <p style={{ color: '#999' }}>请先选择一个项目查看详情，然后可以为该项目生成内部费用记录。</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProjectCosts;